import React, { useState, useEffect, useCallback, useRef } from 'react';
import RichBodyEditor from './RichBodyEditor';

// Admin → Email Manager → Auto emails: emails sent automatically a set time
// after someone joins a community, to those who match the rule (profile
// complete or not, age, their answers). Backend: routes/adminEmailAutomations.js,
// services/emailAutomationService.js; sent hourly by the cron.

const EMPTY = {
  name: '',
  communityId: '',
  delayHours: 24,
  joinedAfter: '',
  joinedBefore: '',
  stopAt: '',
  profile: 'any',
  suggestions: 'any',
  chat: 'any',
  notIfSent: [],
  onlyIfSent: [],
  notIfSubjectHas: '',
  minAge: '',
  maxAge: '',
  conditions: [],
  subject: '',
  body: '',
  includeSignature: true,
  linkStyle: 'text',
  sendAsMe: false,
};

const OPS = [
  { key: 'is', label: 'is' },
  { key: 'isNot', label: 'is not' },
  { key: 'answered', label: 'is answered' },
  { key: 'notAnswered', label: 'is not answered' },
];
const PROFILE = { any: 'Anyone', complete: 'Profile complete', incomplete: 'Profile not complete' };
// Suggestions in the community they joined (backend planAutomation).
const SUGGESTIONS = {
  any: 'Suggestions: any',
  waiting: 'Suggestions available for them (already made, or someone to suggest now)',
  availableNone: "Suggestions available, and they've never been suggested anyone",
  availableMore: "New suggestions available (beyond who they've already been suggested)",
  noneAvailable: 'No suggestions available (no one to suggest yet)',
  some: 'Has been suggested someone',
  none: 'Not been suggested anyone yet',
  noneLiked: "Hasn't liked anyone suggested yet",
};
// Their chat with the community bot (backend planAutomation).
const CHAT = { any: 'Bot chat: any', notOpened: 'Has never opened their bot chat', opened: 'Has opened their bot chat (at least once)' };

// A saved date → the value a datetime-local box shows (local time).
const toLocalInput = (d) => {
  if (!d) return '';
  const x = new Date(d);
  return new Date(x.getTime() - x.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};
// Hours since they joined (whole hours; days too once it's over a day).
const hoursSince = (d) => {
  const h = Math.floor((Date.now() - new Date(d).getTime()) / 3600000);
  return h >= 48 ? `${h} h (${Math.floor(h / 24)} days)` : `${h} h`;
};
const fmt = (d) => (d ? new Date(d).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—');

// The delay is stored in hours, minutes as fractions (0.25 = 15 minutes).
// 24 → "1 day", 2 → "2 hours", 0.25 → "15 minutes", 0 → "straight away"
const delayParts = (h) => {
  const mins = Math.round((Number(h) || 0) * 60);
  return { days: Math.floor(mins / 1440), hours: Math.floor((mins % 1440) / 60), minutes: mins % 60 };
};
const fromParts = ({ days, hours, minutes }) => Math.round((days * 1440 + hours * 60 + minutes) / 60 * 1e6) / 1e6;
const delayText = (h) => {
  const { days, hours, minutes } = delayParts(h);
  const bits = [
    days && `${days} day${days === 1 ? '' : 's'}`,
    hours && `${hours} hour${hours === 1 ? '' : 's'}`,
    minutes && `${minutes} minute${minutes === 1 ? '' : 's'}`,
  ].filter(Boolean);
  return bits.length ? bits.join(' ') : 'straight away';
};

// Location / photo: there or not, so "is added" / "is missing".
const PRESENCE_LABELS = { answered: 'is added', notAnswered: 'is missing' };
const PRESENCE_FIELDS = ['__location', '__photo'];

const conditionText = (c) => {
  const op = (PRESENCE_FIELDS.includes(c.field) && PRESENCE_LABELS[c.op]) || OPS.find(o => o.key === c.op)?.label || c.op;
  const field = { gender: 'Gender', __location: 'Location', __photo: 'Profile photo' }[c.field] || c.field;
  return ['answered', 'notAnswered'].includes(c.op) ? `${field} ${op}` : `${field} ${op} ${(c.values || []).join(' or ') || '…'}`;
};

const ruleSummary = (r, communities, rules = []) => {
  const nameOf = (id) => rules.find(x => x._id === id)?.name || 'another email';
  const where = r.communityId === 'all' ? 'any community' : (communities.find(c => c._id === r.communityId)?.title || 'a community');
  const who = [
    r.joinedAfter ? (r.joinedBefore ? `joined ${fmt(r.joinedAfter)} – ${fmt(r.joinedBefore)}` : `joined since ${fmt(r.joinedAfter)}`) : null,
    r.stopAt ? `${new Date(r.stopAt) < new Date() ? 'ended' : 'until'} ${fmt(r.stopAt)}` : null,
    r.profile !== 'any' ? PROFILE[r.profile].toLowerCase() : null,
    r.chat && r.chat !== 'any' ? CHAT[r.chat].toLowerCase() : null,
    r.suggestions && r.suggestions !== 'any' ? SUGGESTIONS[r.suggestions].toLowerCase() : null,
    (r.notIfSent || []).length ? `not if had ${r.notIfSent.map(id => `“${nameOf(id)}”`).join(' or ')}` : null,
    r.notIfSubjectHas ? `not if already had a “${r.notIfSubjectHas}” email from the group` : null,
    (r.onlyIfSent || []).length ? `only if had ${r.onlyIfSent.map(id => `“${nameOf(id)}”`).join(' and ')}` : null,
    r.minAge || r.maxAge ? `aged ${r.minAge || '?'}–${r.maxAge || '?'}` : null,
    ...(r.conditions || []).map(conditionText),
  ].filter(Boolean);
  return `${delayText(r.delayHours)} after joining ${where}${who.length ? ` · ${who.join(' · ')}` : ''}`;
};

const AutoEmailsPanel = ({ authedFetch, communities }) => {
  const [rules, setRules] = useState(null);
  const [placeholders, setPlaceholders] = useState([]);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null); // { id|null, draft }
  const [sentFor, setSentFor] = useState(null); // { id, name, rows }
  const [busy, setBusy] = useState('');
  const [note, setNote] = useState('');

  const load = useCallback(async () => {
    try {
      const d = await authedFetch('/email/automations');
      setRules(d.automations);
      setPlaceholders(d.placeholders || []);
    } catch (e) { setError(e.message); }
  }, [authedFetch]);
  useEffect(() => { load(); }, [load]);

  const toggle = async (r) => {
    const on = !r.enabled;
    const reach = r.joinedAfter
      ? `It will email everyone who joined ${r.joinedBefore ? `between ${fmt(r.joinedAfter)} and ${fmt(r.joinedBefore)}` : `since ${fmt(r.joinedAfter)}`} and matches — including people who joined before today, as soon as it runs.`
      : `It will email people who join from now on, ${delayText(r.delayHours)} after they join, if they match. People who joined before now won't get it.`;
    if (on && !window.confirm(`Switch on "${r.name}"?\n\n${reach}\n\nUse "Who would get it?" in Edit to check the list first.`)) return;
    setBusy(r._id);
    try {
      await authedFetch(`/email/automations/${r._id}/enabled`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ enabled: on }) });
      await load();
    } catch (e) { setError(e.message); } finally { setBusy(''); }
  };

  const archive = async (r) => {
    if (!window.confirm(`Remove "${r.name}"? It stops sending; its send log is kept.`)) return;
    try {
      await authedFetch(`/email/automations/${r._id}`, { method: 'DELETE' });
      await load();
    } catch (e) { setError(e.message); }
  };

  // "Send due now" waits 60 seconds first, with a Cancel button — nothing
  // is sent until the countdown ends (and nothing if this page is closed).
  const SEND_DELAY_S = 60;
  const [pendingSend, setPendingSend] = useState(null); // { id, name, count, left }
  const pendingRef = useRef(null);
  useEffect(() => () => clearInterval(pendingRef.current?.timer), []);
  const startSend = async (r) => {
    if (pendingRef.current) return;
    let count = null;
    try {
      const pv = await authedFetch('/email/automations/preview', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: r._id }) });
      count = pv.dueCount;
    } catch { /* still allow sending */ }
    if (count === 0) { setNote(`"${r.name}": no one is due right now — nothing to send.`); return; }
    // The countdown lives in a ref (not a state updater) so the send can
    // only ever fire once.
    const pending = { left: SEND_DELAY_S, timer: null };
    pending.timer = setInterval(() => {
      if (pendingRef.current !== pending) { clearInterval(pending.timer); return; }
      pending.left -= 1;
      if (pending.left <= 0) {
        clearInterval(pending.timer);
        pendingRef.current = null;
        setPendingSend(null);
        runNow(r);
        return;
      }
      setPendingSend({ id: r._id, name: r.name, count, left: pending.left });
    }, 1000);
    pendingRef.current = pending;
    setPendingSend({ id: r._id, name: r.name, count, left: SEND_DELAY_S });
  };
  const cancelSend = () => {
    clearInterval(pendingRef.current?.timer);
    pendingRef.current = null;
    setPendingSend(null);
    setNote('Cancelled — nothing was sent.');
  };

  const runNow = async (r) => {
    setBusy(r._id);
    try {
      const d = await authedFetch(`/email/automations/${r._id}/run`, { method: 'POST' });
      setNote(`"${r.name}": ${d.sent || 0} sent${d.failed ? `, ${d.failed} failed` : ''}${d.dry_run ? `, ${d.dry_run} not really sent (no email key here)` : ''}${d.suppressed ? `, ${d.suppressed} unsubscribed` : ''}.`);
      await load();
    } catch (e) { setError(e.message); } finally { setBusy(''); }
  };

  // A copy of a rule to adapt (e.g. another community): starts off, with a
  // fresh send log — the original is untouched.
  const duplicate = async (r) => {
    const copy = {
      name: `Copy of ${r.name}`.trim(),
      communityId: r.communityId, delayHours: r.delayHours, joinedAfter: r.joinedAfter, joinedBefore: r.joinedBefore, stopAt: r.stopAt,
      profile: r.profile, suggestions: r.suggestions, chat: r.chat, notIfSent: r.notIfSent, onlyIfSent: r.onlyIfSent, notIfSubjectHas: r.notIfSubjectHas, minAge: r.minAge, maxAge: r.maxAge, conditions: r.conditions,
      subject: r.subject, body: r.body, includeSignature: r.includeSignature, sendAsMe: r.sendAsMe, linkStyle: r.linkStyle,
    };
    try {
      const d = await authedFetch('/email/automations', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(copy) });
      const c = d.automation;
      setNote(`Made "${c.name}" (off). Edit it, then switch it on when ready.`);
      await load();
      setEditing({ id: c._id, draft: { ...EMPTY, ...c, minAge: c.minAge ?? '', maxAge: c.maxAge ?? '', joinedAfter: toLocalInput(c.joinedAfter), joinedBefore: toLocalInput(c.joinedBefore), stopAt: toLocalInput(c.stopAt) } });
    } catch (e) { setError(e.message); }
  };

  const showSent = async (r) => {
    try {
      const d = await authedFetch(`/email/automations/${r._id}/sent`);
      setSentFor({ id: r._id, name: r.name, rows: d.sent });
    } catch (e) { setError(e.message); }
  };

  if (editing) {
    return (
      <AutoEmailEditor
        authedFetch={authedFetch}
        communities={communities}
        placeholders={placeholders}
        initial={editing}
        otherRules={(rules || []).filter(r => r._id !== editing.id).map(r => ({ _id: r._id, name: r.name }))}
        onClose={async () => { setEditing(null); await load(); }}
      />
    );
  }

  return (
    <div className="ae-panel">
      {error && <div className="as-error" onClick={() => setError('')}>{error}</div>}
      {note && <div className="ae-note" onClick={() => setNote('')}>{note}</div>}
      <div className="ae-head">
        <p className="ae-intro">
          Emails sent automatically after someone joins, to those who match. A rule emails people who join
          <b> after</b> it's switched on, or everyone who joined in its date range if you set one. Rules are checked every hour.
        </p>
        <button className="as-btn as-btn--primary" onClick={() => setEditing({ id: null, draft: { ...EMPTY } })}>+ New auto email</button>
      </div>

      {rules === null && <div className="as-empty">Loading…</div>}
      {rules?.length === 0 && <div className="as-empty">No auto emails yet.</div>}

      {rules?.map(r => (
        <div key={r._id} className={`ae-rule${r.enabled ? ' ae-rule--on' : ''}`}>
          <label className="ae-switch" title={r.enabled ? 'On — click to switch off' : 'Off — click to switch on'}>
            <input type="checkbox" checked={!!r.enabled} disabled={busy === r._id} onChange={() => toggle(r)} />
            <span />
          </label>
          <div className="ae-rule-main">
            <div className="ae-rule-name">{r.name} {r.enabled && r.stopAt && new Date(r.stopAt) < new Date()
              ? <span className="ae-pill">Ended</span>
              : <span className={`ae-pill${r.enabled ? ' ae-pill--on' : ''}`}>{r.enabled ? 'On' : 'Off'}</span>}</div>
            <div className="ae-rule-when">{ruleSummary(r, communities, rules || [])}</div>
            <div className="ae-rule-subject">“{r.subject || 'No subject yet'}”</div>
            <div className="ae-rule-stats">
              {r.stats.sent} sent{r.stats.failed ? ` · ${r.stats.failed} failed` : ''}
              {r.stats.lastSentAt ? ` · last ${fmt(r.stats.lastSentAt)}` : ''}
              {!r.joinedAfter && r.startFrom ? ` · for people joining since ${fmt(r.startFrom)}` : ''}
            </div>
          </div>
          <div className="ae-rule-actions">
            <button className="as-btn" onClick={() => setEditing({ id: r._id, draft: { ...EMPTY, ...r, minAge: r.minAge ?? '', maxAge: r.maxAge ?? '', joinedAfter: toLocalInput(r.joinedAfter), joinedBefore: toLocalInput(r.joinedBefore), stopAt: toLocalInput(r.stopAt) } })}>Edit</button>
            <button className="as-btn" onClick={() => duplicate(r)}>Duplicate</button>
            <button className="as-btn" onClick={() => showSent(r)}>Sent to</button>
            {r.enabled && pendingSend?.id !== r._id && <button className="as-btn" disabled={busy === r._id || !!pendingSend} onClick={() => startSend(r)}>Send due now</button>}
            <button className="as-btn ae-remove" onClick={() => archive(r)}>Remove</button>
          </div>
          {pendingSend?.id === r._id && (
            <div className="ae-countdown">
              <span>
                Sending to <b>{pendingSend.count ?? 'everyone due'}{pendingSend.count != null ? ` ${pendingSend.count === 1 ? 'person' : 'people'}` : ''}</b> in <b>{pendingSend.left}s</b>… (keep this page open)
              </span>
              <button className="as-btn ae-cancel" onClick={cancelSend}>Cancel</button>
            </div>
          )}
          {sentFor?.id === r._id && (
            <div className="ae-sent">
              <div className="ae-sent-head">Sent to <button className="ae-link" onClick={() => setSentFor(null)}>close</button></div>
              {sentFor.rows.length === 0 ? <div className="ae-muted">No one yet.</div> : (
                <table className="ae-table">
                  <tbody>
                    {sentFor.rows.map(s => (
                      <tr key={s._id}>
                        <td>{s.firstNameSnapshot || '—'}</td>
                        <td>{s.recipientEmail}</td>
                        <td>{s.status}{s.errorMessage ? ` (${s.errorMessage})` : ''}</td>
                        <td>{fmt(s.sentAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

const AutoEmailEditor = ({ authedFetch, communities, placeholders, initial, otherRules = [], onClose }) => {
  const [id, setId] = useState(initial.id);
  const [d, setD] = useState(initial.draft);
  const [fields, setFields] = useState([]);
  const [preview, setPreview] = useState(null);
  const [live, setLive] = useState(null); // the email as written, filled in for one person
  const [error, setError] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  // Result of the last action button (save / test / who), shown beside the buttons.
  const [status, setStatus] = useState(null); // { kind: 'busy' | 'ok' | 'error', text }
  const set = (k, v) => setD(prev => ({ ...prev, [k]: v }));
  // Fill-ins go where the cursor last was: the subject or the email.
  const subjectRef = useRef(null);
  const bodyInsertRef = useRef(null);
  const lastFieldRef = useRef('body');
  const insertFillIn = (tag) => {
    if (lastFieldRef.current === 'subject' && subjectRef.current) {
      const el = subjectRef.current;
      const v = d.subject || '';
      const start = el.selectionStart ?? v.length;
      const end = el.selectionEnd ?? v.length;
      set('subject', v.slice(0, start) + tag + v.slice(end));
      requestAnimationFrame(() => { el.focus(); el.setSelectionRange(start + tag.length, start + tag.length); });
    } else if (bodyInsertRef.current) {
      bodyInsertRef.current(tag);
    }
  };

  useEffect(() => {
    if (!d.communityId) return;
    authedFetch(`/email/automations/fields?communityId=${encodeURIComponent(d.communityId)}`)
      .then(r => setFields(r.fields || []))
      .catch(e => setError(e.message));
  }, [d.communityId, authedFetch]);

  const payload = () => ({
    ...d,
    delayHours: Number(d.delayHours) || 0,
    joinedAfter: d.joinedAfter ? new Date(d.joinedAfter).toISOString() : null,
    joinedBefore: d.joinedAfter && d.joinedBefore ? new Date(d.joinedBefore).toISOString() : null,
    stopAt: d.stopAt ? new Date(d.stopAt).toISOString() : null,
    minAge: d.minAge === '' ? null : Number(d.minAge),
    maxAge: d.maxAge === '' ? null : Number(d.maxAge),
  });
  const json = (body) => ({ method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });

  const save = async (close) => {
    if (!d.name.trim()) { setStatus({ kind: 'error', text: 'Give it a name.' }); return; }
    if (!d.communityId) { setStatus({ kind: 'error', text: 'Pick a community (under "When").' }); return; }
    setSaving(true);
    setError('');
    try {
      const r = id
        ? await authedFetch(`/email/automations/${id}`, { ...json(payload()), method: 'PUT' })
        : await authedFetch('/email/automations', json(payload()));
      setId(r.automation._id);
      setStatus({ kind: 'ok', text: '✓ Saved.' });
      if (close) onClose();
    } catch (e) { setError(e.message); } finally { setSaving(false); }
  };

  const runPreview = async () => {
    setError('');
    setStatus({ kind: 'busy', text: 'Finding who would get it…' });
    try {
      const r = await authedFetch('/email/automations/preview', json({ id, rule: payload() }));
      setPreview(r);
      setStatus({ kind: 'ok', text: `✓ ${r.dueCount} due now, ${r.upcomingCount} later — see the right-hand panel.` });
    } catch (e) { setStatus({ kind: 'error', text: e.message }); }
  };

  // "Fix spelling & grammar": corrects only spelling, grammar and
  // punctuation (backend services/proofreadService.js), with Undo.
  const [undoText, setUndoText] = useState(null); // { subject, body } before the last fix
  const fixSpelling = async () => {
    setStatus({ kind: 'busy', text: 'Checking spelling and grammar…' });
    try {
      const before = { subject: d.subject, body: d.body };
      const r = await authedFetch('/email/proofread', json(before));
      if (!r.changes?.length || (r.subject === before.subject && r.body === before.body)) {
        setStatus({ kind: 'ok', text: '✓ No spelling or grammar mistakes found.' });
        return;
      }
      setUndoText(before);
      setD(prev => ({ ...prev, subject: r.subject, body: r.body }));
      setStatus({ kind: 'ok', text: `✓ Fixed: ${r.changes.join(' · ')}` });
    } catch (e) { setStatus({ kind: 'error', text: e.message }); }
  };
  const undoFix = () => {
    if (!undoText) return;
    setD(prev => ({ ...prev, ...undoText }));
    setUndoText(null);
    setStatus({ kind: 'ok', text: 'Undone — back to your original text.' });
  };

  const sendTest = async () => {
    setError('');
    setStatus({ kind: 'busy', text: 'Sending you a test…' });
    try {
      const r = await authedFetch('/email/automations/test', json({ id, rule: payload() }));
      setStatus(r.status === 'sent'
        ? { kind: 'ok', text: `✓ Test sent to ${r.to} (filled in as for ${r.filledInFor}) — check your inbox.` }
        : r.status === 'dry_run'
          ? { kind: 'error', text: 'Not really sent — no email key on this server.' }
          : { kind: 'error', text: `Test failed: ${r.errorMessage}` });
    } catch (e) { setStatus({ kind: 'error', text: e.message }); }
  };

  // Live preview: re-rendered (by the server, so it's exactly what's sent)
  // a moment after each change, for the first person in the "Who would get
  // it?" list, or an example member.
  const sampleWho = preview?.due?.[0] || preview?.upcoming?.[0] || null;
  useEffect(() => {
    if (!d.communityId) return undefined;
    const t = setTimeout(() => {
      authedFetch('/email/automations/render', json({
        rule: { communityId: d.communityId, subject: d.subject, body: d.body, includeSignature: d.includeSignature, sendAsMe: d.sendAsMe, linkStyle: d.linkStyle },
        person: sampleWho,
      })).then(setLive).catch(() => {});
    }, 500);
    return () => clearTimeout(t);
  }, [d.communityId, d.subject, d.body, d.includeSignature, d.sendAsMe, d.linkStyle, sampleWho, authedFetch]); // eslint-disable-line react-hooks/exhaustive-deps

  const setCondition = (i, patch) => set('conditions', d.conditions.map((c, j) => (j === i ? { ...c, ...patch } : c)));
  const fieldInfo = (name) => fields.find(f => f.name === name);


  return (
    <div className="ae-editor">
      <button className="ae-link" onClick={onClose}>← Back to auto emails</button>
      {error && <div className="as-error" onClick={() => setError('')}>{error}</div>}
      {note && <div className="ae-note" onClick={() => setNote('')}>{note}</div>}

      <div className="ae-grid">
        <div className="ae-form">
          <label className="ae-field">
            <span>Name (only you see this)</span>
            <input value={d.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Golfers: finish your profile" />
          </label>

          <div className="ae-section">When</div>
          <div className="ae-row">
            {/* Days, hours and minutes after they join (checked every 10 minutes). */}
            <input type="number" min="0" className="ae-num" value={delayParts(d.delayHours).days}
              onChange={e => set('delayHours', fromParts({ ...delayParts(d.delayHours), days: Math.max(0, Math.floor(Number(e.target.value) || 0)) }))} />
            <span>days</span>
            <input type="number" min="0" max="23" className="ae-num" value={delayParts(d.delayHours).hours}
              onChange={e => set('delayHours', fromParts({ ...delayParts(d.delayHours), hours: Math.min(23, Math.max(0, Math.floor(Number(e.target.value) || 0))) }))} />
            <span>hours</span>
            <input type="number" min="0" max="59" step="5" className="ae-num" value={delayParts(d.delayHours).minutes}
              onChange={e => set('delayHours', fromParts({ ...delayParts(d.delayHours), minutes: Math.min(59, Math.max(0, Math.floor(Number(e.target.value) || 0))) }))} />
            <span>minutes</span>
            <span>after they join</span>
            <select value={d.communityId} onChange={e => { set('communityId', e.target.value); set('conditions', []); }}>
              {!d.communityId && <option value="">Pick a community…</option>}
              <option value="all">any community</option>
              {communities.map(c => <option key={c._id} value={c._id}>{c.title}</option>)}
            </select>
          </div>

          <div className="ae-row">
            <span>Stop sending after</span>
            <input type="datetime-local" value={d.stopAt || ''} onChange={e => set('stopAt', e.target.value)} />
            {d.stopAt && <button type="button" className="ae-link" onClick={() => set('stopAt', '')}>clear</button>}
          </div>
          <div className="ae-hint">
            {d.stopAt
              ? `Sends nothing after ${fmt(d.stopAt)}, even if it's still switched on.`
              : 'Optional — leave empty to keep sending until you switch it off.'}
          </div>

          <div className="ae-section">Who gets it</div>
          <div className="ae-row">
            <select value={d.profile} onChange={e => set('profile', e.target.value)}>
              {Object.entries(PROFILE).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <span>aged</span>
            <input type="number" className="ae-num" placeholder="any" value={d.minAge} onChange={e => set('minAge', e.target.value)} />
            <span>to</span>
            <input type="number" className="ae-num" placeholder="any" value={d.maxAge} onChange={e => set('maxAge', e.target.value)} />
          </div>
          <div className="ae-row">
            <select value={d.chat || 'any'} onChange={e => set('chat', e.target.value)}>
              {Object.entries(CHAT).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <select value={d.suggestions || 'any'} onChange={e => set('suggestions', e.target.value)}>
              {Object.entries(SUGGESTIONS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div className="ae-row">
            <span>Not if they&apos;ve already had an email from that group with</span>
            <input className="ae-text-sm" placeholder="e.g. welcome" value={d.notIfSubjectHas || ''} onChange={e => set('notIfSubjectHas', e.target.value)} />
            <span>in the subject</span>
          </div>
          {otherRules.length > 0 && (
            <>
              <div className="ae-row ae-row--wrap">
                <span>Not if they&apos;ve already had:</span>
                {otherRules.map(r => (
                  <label key={r._id} className="ae-chk">
                    <input type="checkbox" checked={(d.notIfSent || []).includes(r._id)}
                      onChange={e => set('notIfSent', e.target.checked ? [...(d.notIfSent || []), r._id] : (d.notIfSent || []).filter(x => x !== r._id))} />
                    {r.name}
                  </label>
                ))}
              </div>
              <div className="ae-row ae-row--wrap">
                <span>Only if they&apos;ve already had:</span>
                {otherRules.map(r => (
                  <label key={r._id} className="ae-chk">
                    <input type="checkbox" checked={(d.onlyIfSent || []).includes(r._id)}
                      onChange={e => set('onlyIfSent', e.target.checked ? [...(d.onlyIfSent || []), r._id] : (d.onlyIfSent || []).filter(x => x !== r._id))} />
                    {r.name}
                  </label>
                ))}
              </div>
            </>
          )}
          <div className="ae-hint">
            Combine the two: &ldquo;Has never opened their bot chat&rdquo; + &ldquo;Suggestions available&rdquo; = people with suggestions waiting who never reached Messenger;
            &ldquo;Has opened their bot chat&rdquo; + &ldquo;Has been suggested someone&rdquo; = they&apos;ve seen suggestions;
            &ldquo;Has opened their bot chat&rdquo; + &ldquo;No suggestions available&rdquo; = they looked and there was no one yet.
          </div>
          {d.suggestions === 'availableMore' && (
            <div className="ae-hint">
              They&apos;ve been suggested people before, and there&apos;s someone new the bot could suggest now — never anyone they&apos;ve already been suggested or introduced to.
            </div>
          )}
          {d.suggestions === 'availableNone' && (
            <div className="ae-hint">
              Someone could be suggested to them right now (another member with a ready profile within both people&apos;s distance limits), and nobody has ever been suggested to them — their first suggestions are waiting.
            </div>
          )}
          {d.suggestions === 'waiting' && (
            <div className="ae-hint">
              Someone has already been suggested to them, or the bot could suggest someone right now (another member with a ready profile within both people&apos;s distance limits).
              With &ldquo;Has never opened their bot chat&rdquo;, that&apos;s people with suggestions waiting who never got as far as Messenger.
            </div>
          )}
          {d.suggestions === 'noneAvailable' && (
            <div className="ae-hint">
              Nobody the bot could suggest to them right now: no other member with a ready profile within both people&apos;s distance limits whom they haven&apos;t already been suggested.
              It&apos;s checked when the email is due, so set the delay to give the bot a chance first.
            </div>
          )}
          <div className="ae-row">
            <label>
              <input type="checkbox" checked={!!d.joinedAfter}
                onChange={e => { set('joinedAfter', e.target.checked ? toLocalInput(new Date(new Date().setHours(0, 0, 0, 0) - 7 * 86400000)) : ''); if (!e.target.checked) set('joinedBefore', ''); }} />
              Send only to people who joined in a date range (including people who have already joined)
            </label>
          </div>
          {d.joinedAfter && (
            <div className="ae-row">
              <span>Joined between</span>
              <input type="datetime-local" value={d.joinedAfter} onChange={e => set('joinedAfter', e.target.value)} />
              <span>and</span>
              <input type="datetime-local" value={d.joinedBefore || ''} min={d.joinedAfter} onChange={e => set('joinedBefore', e.target.value)} />
              {d.joinedBefore
                ? <button type="button" className="ae-link" onClick={() => set('joinedBefore', '')}>clear end</button>
                : <span className="ae-muted">(no end date: anyone since)</span>}
            </div>
          )}
          <div className="ae-hint">
            {d.joinedAfter
              ? (d.joinedBefore
                ? `Everyone who joined between ${fmt(d.joinedAfter)} and ${fmt(d.joinedBefore)} and matches gets it — including people who joined before it's switched on.`
                : `Everyone who joined since ${fmt(d.joinedAfter)} and matches gets it (once the delay has passed) — including people who joined before it's switched on.`)
              : 'Unticked: only people who join after it is switched on get it.'}
          </div>
          <div className="ae-hint">"Profile complete" means ready to be suggested people (the same check as suggestions use).</div>

          {d.conditions.map((c, i) => {
            const f = fieldInfo(c.field);
            return (
              <div key={i} className="ae-cond">
                <select value={c.field} onChange={e => setCondition(i, { field: e.target.value, values: [], ...(fieldInfo(e.target.value)?.presenceOnly && ['is', 'isNot'].includes(c.op) ? { op: 'notAnswered' } : {}) })}>
                  {fields.map(x => <option key={x.name} value={x.name}>{x.label || x.name}</option>)}
                </select>
                <select value={c.op} onChange={e => setCondition(i, { op: e.target.value })}>
                  {/* Location / photo: only whether they have one. */}
                  {OPS.filter(o => !f?.presenceOnly || ['answered', 'notAnswered'].includes(o.key)).map(o => <option key={o.key} value={o.key}>{(f?.presenceOnly && PRESENCE_LABELS[o.key]) || o.label}</option>)}
                </select>
                <button className="ae-link" onClick={() => set('conditions', d.conditions.filter((_, j) => j !== i))}>remove</button>
                {['is', 'isNot'].includes(c.op) && (
                  f?.options ? (
                    <div className="ae-chips">
                      {f.options.map(o => {
                        const onIt = (c.values || []).includes(o);
                        return (
                          <button key={o} type="button" className={`ae-chip${onIt ? ' ae-chip--on' : ''}`}
                            onClick={() => setCondition(i, { values: onIt ? c.values.filter(v => v !== o) : [...(c.values || []), o] })}>{o}</button>
                        );
                      })}
                    </div>
                  ) : (
                    <input className="ae-cond-text" placeholder="value (several: separate with commas)" value={(c.values || []).join(', ')}
                      onChange={e => setCondition(i, { values: e.target.value.split(',').map(v => v.trim()).filter(Boolean) })} />
                  )
                )}
              </div>
            );
          })}
          <button className="ae-link" disabled={!fields.length} onClick={() => set('conditions', [...d.conditions, { field: fields[0]?.name, op: 'is', values: [] }])}>+ Add a condition on their answers</button>
          {d.communityId === 'all' && <div className="ae-hint">For "any community" only gender can be used — each community asks different questions.</div>}

          <div className="ae-section">The email</div>
          <label className="ae-field">
            <span>Subject</span>
            <input ref={subjectRef} onFocus={() => { lastFieldRef.current = 'subject'; }} value={d.subject} onChange={e => set('subject', e.target.value)} placeholder="e.g. {{firstName}}, a couple of quick things for {{communityName}}" />
          </label>
          <div onFocusCapture={() => { lastFieldRef.current = 'body'; }}>
            <RichBodyEditor value={d.body} onChange={v => set('body', v)} placeholder="Write the email…" insertRef={bodyInsertRef} />
          </div>
          <div className="ae-fillins">
            <div className="ae-fillins-title">Fill-ins — click one to put it where your cursor is (subject or email)</div>
            {placeholders.map(p => (
              <React.Fragment key={p.tag}>
                <button type="button" className="ae-chip"
                  onMouseDown={e => e.preventDefault()} onClick={() => insertFillIn(p.tag)}><code>{p.tag}</code></button>
                <span>{p.about}</span>
              </React.Fragment>
            ))}
          </div>
          <div className="ae-row">
            <label><input type="checkbox" checked={d.includeSignature} onChange={e => set('includeSignature', e.target.checked)} /> Add my signature</label>
          </div>
          <div className="ae-row">
            <span>Links show as</span>
            <select value={d.linkStyle || 'text'} onChange={e => set('linkStyle', e.target.value)}>
              <option value="text">Text link (best for Gmail's Primary tab)</option>
              <option value="button">Button</option>
              <option value="buttonWithPic">Button with the community picture (chat link)</option>
            </select>
          </div>
          <div className="ae-row">
            <span>From</span>
            <select value={d.sendAsMe ? 'me' : 'community'} onChange={e => set('sendAsMe', e.target.value === 'me')}>
              <option value="community">"{'{Community}'} Interconnector" (e.g. UK Golfers Interconnector)</option>
              <option value="me">"James at {'{Community}'}"</option>
            </select>
          </div>

          <div className="ae-row ae-actions">
            <button className="as-btn as-btn--primary" disabled={saving} onClick={() => save(true)}>{saving ? 'Saving…' : 'Save'}</button>
            <button className="as-btn" disabled={saving} onClick={() => save(false)}>Save, keep editing</button>
            <button className="as-btn" onClick={runPreview}>Who would get it?</button>
            <button className="as-btn" onClick={sendTest}>Send me a test</button>
            <button className="as-btn" onClick={fixSpelling}>✓ Fix spelling &amp; grammar</button>
            {undoText && <button className="as-btn" onClick={undoFix}>Undo fix</button>}
          </div>
          {status && <div className={`ae-status ae-status--${status.kind}`}>{status.text}</div>}
          <div className="ae-hint">New rules start off. Switch them on from the list.</div>
        </div>

        <div className="ae-preview">
          <div className="ae-section">Preview{live ? ` — as ${live.to} would see it` : ''}</div>
          {live ? (
            <>
              <div className="ae-sample-subject">{live.subject || <span className="ae-muted">(no subject yet)</span>}</div>
              <iframe title="Email preview" className="ae-sample" srcDoc={live.htmlBody} />
            </>
          ) : <div className="ae-muted">Pick a community and start writing — the email shows here.</div>}
          <div className="ae-divider" />
          {!preview && <div className="ae-muted">Click "Who would get it?" to see who matches.</div>}
          {preview && (
            <>
              <div className="ae-section">
                {preview.joinedAfter ? (preview.joinedBefore ? `Everyone who joined ${fmt(preview.joinedAfter)} – ${fmt(preview.joinedBefore)}` : `Everyone who joined since ${fmt(preview.joinedAfter)}`) : preview.asIfOn ? 'If it had been on for the last 60 days' : `Only people who joined since it was switched on (${fmt(preview.from)}). To include people who joined earlier, tick the date range.`}
              </div>
              <div className="ae-counts">
                <span><b>{preview.dueCount}</b> due now</span>
                <span><b>{preview.upcomingCount}</b> due later</span>
                <span className="ae-muted">left out: {preview.skipped.notMatching} don't match · {preview.skipped.alreadySent} already sent · {preview.skipped.noEmail} no email</span>
              </div>
              {[...preview.due.map(p => ({ ...p, when: 'due' })), ...preview.upcoming.map(p => ({ ...p, when: 'later' }))].length > 0 && (
                <table className="ae-table">
                  <thead><tr><th>Name</th><th>Joined</th><th>Hours since joining</th><th>Gets it</th></tr></thead>
                  <tbody>
                    {[...preview.due.map(p => ({ ...p, when: 'due' })), ...preview.upcoming.map(p => ({ ...p, when: 'later' }))].slice(0, 30).map(p => (
                      <tr key={`${p.userId}-${p.communityName}`}>
                        <td>{p.firstName || p.email}{p.age ? `, ${p.age}` : ''}{d.communityId === 'all' ? <div className="ae-muted">{p.communityName}</div> : null}</td>
                        <td>{fmt(p.joinedAt)}</td>
                        <td>{hoursSince(p.joinedAt)}</td>
                        <td>{p.when === 'due' ? (preview.asIfOn ? 'would have' : 'next run') : fmt(p.dueAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AutoEmailsPanel;
